type Level = 'info' | 'warn' | 'error';

type ErrorReason = {
	readonly [key: string]: string | number | boolean | undefined | void | null;
};

type Event = Error | ErrorReason;

const defaultOptions: RequestInit = {
	credentials: 'same-origin',
	headers: {
		'Content-Type': 'application/json',
		Accept: 'application/json, text/javascript, */*',
	},
};

const isProduction = (): boolean => process.env.NODE_ENV === 'production';

const MAX_REQUESTS_PER_INTERVAL = 5;
const INTERVAL_MS = 1000;

let requestQueue: { body: string; endpoint: string }[] = [];
let isProcessingQueue = false;

const processQueue = () => {
	if (isProcessingQueue) {
		return;
	}
	isProcessingQueue = true;
	const processNextBatch = () => {
		if (requestQueue.length === 0) {
			isProcessingQueue = false;
			return;
		}
		const batch = requestQueue.splice(0, MAX_REQUESTS_PER_INTERVAL);
		batch.forEach(({ body, endpoint }) => {
			fetch(endpoint, {
				...defaultOptions,
				method: 'POST',
				body,
			});
		});
		setTimeout(processNextBatch, INTERVAL_MS);
	};

	processNextBatch();
};

const sendToServerBatched = (body: string, endpoint: string) => {
	requestQueue.push({ body, endpoint });
	// Start processing the logging queue if not already started
	processQueue();
};

const sendToConsole = (
	body: string,
	endpoint: string,
	level: Level,
	message: string,
	eventType: string,
	event: unknown,
) => {
	// eslint-disable-next-line no-console
	console[level](
		'%c%s %c(POST %s)\n%cMessage\n%c%s\n\n%cEvent body\n%c%s\n\n%cException object and Stack trace (expand to jump to source)\n',
		'font-size: 14px; font-weight: bold;',
		level.toUpperCase(),
		'font-size: 10px; font-family: monospace;',
		endpoint,
		'font-family: sans-serif; font-weight: bold; font-size: 12px',
		'font-family: sans-serif; font-weight: initial;',
		message,
		'font-family: sans-serif; font-weight: bold;',
		'font-family: monospace; font-weight: initial;',
		body,
		'font-family: sans-serif; font-size: 12px; font-weight: bold;',
		event,
	);

	const currentWindow: any = window;
	if (!isProduction() && currentWindow.Cypress) {
		if (!currentWindow.cypressSafeErrorsLog) {
			currentWindow.cypressSafeErrorsLog = [];
		}

		currentWindow.cypressSafeErrorsLog.push({
			body,
			endpoint,
			level,
			message,
		});
	}

	if (eventType !== 'object') {
		const errorMessage = `Invalid log API usage: expected event of type 'object', but was ${eventType}`;
		// eslint-disable-next-line no-console
		console.error(errorMessage);
	}

	if (typeof body !== 'string') {
		const errorMessage = `Invalid log API usage: expected event of type 'string', but was ${eventType}; message will be replaced by: ${String(
			message,
		)}`;
		// eslint-disable-next-line no-console
		console.error(errorMessage);
	}

	return Promise.resolve();
};

const transformData = (location: string, message: string, event?: Event) => {
	return {
		sentFrom: location,
		message: String(message),
		event,
		buildKey: (window as any)?.BUILD_KEY,
		logTime: new Date().toISOString(),
	};
};

const sendData = (level: Level, location: string, message: string, event: Event = {}) => {
	const data = transformData(location, message, event);
	const body = JSON.stringify(data);
	const endpoint = `/rest/internal/2/log/safe/${level}`;

	if (isProduction()) {
		return sendToServerBatched(body, endpoint);
	}
	return sendToConsole(body, endpoint, level, message, typeof event, event);
};

export const safeWarnWithoutCustomerData = (location: string, message: string, event?: Event) => {
	sendData('warn', location, message, event);
};
